/**
 * Class library (CC redesign brief 3, Nino approved 6 Oct): four statuses
 * said plainly, and what's missing, each with its one fix.
 *
 *   Draft            something's missing
 *   Ready to publish generated and audited, not published yet
 *   Published        on the shelf: learners see it
 *   Off the shelf    hidden from learners, nothing lost (Scoring, 7 Oct)
 */
import type { MicroClassListItem } from '@/types/micro-class-builder';

export type ClassStatus = 'draft' | 'ready' | 'published' | 'offshelf';

export type ClassFixKind = 'resume' | 'audit' | 'why';

export interface ClassMissing {
  text: string;
  fix?: string;
  kind?: ClassFixKind;
  red?: boolean;
}

/** A class row: a draft, a published class, or one taken off the shelf. */
export type ClassRow = MicroClassListItem & { off_shelf?: boolean };

const hasContent = (c: ClassRow) => c.has_content ?? !!c.generated_content;

export function classStatusOf(c: ClassRow): ClassStatus {
  if (c.off_shelf) return 'offshelf';
  if (c.status === 'published') return 'published';
  return classMissingOf(c).length > 0 ? 'draft' : 'ready';
}

export function classMissingOf(c: ClassRow): ClassMissing[] {
  if (c.off_shelf || c.status === 'published') return [];
  if (c.status === 'generating') return [{ text: 'Generating…' }];
  if (!hasContent(c)) return [{ text: 'Not generated yet', fix: 'Resume', kind: 'resume' }];
  if (!c.audit_gate) return [{ text: 'Audit not run', fix: 'Run audit', kind: 'audit' }];
  // The server refuses to publish a class whose audit is red.
  if (c.audit_gate === 'red') return [{ text: 'Audit says fix', fix: 'See why', kind: 'why', red: true }];
  return [];
}

const ORDER: Record<ClassStatus, number> = { draft: 0, ready: 1, published: 2, offshelf: 3 };

/** Needs work first: drafts, then ready, published, off the shelf; newest first within. */
export function classByNeedsWork(a: ClassRow, b: ClassRow): number {
  const s = ORDER[classStatusOf(a)] - ORDER[classStatusOf(b)];
  if (s) return s;
  return (Date.parse(b.created_at ?? '') || 0) - (Date.parse(a.created_at ?? '') || 0);
}
